import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { ThemeContext, type ThemeMode } from './theme-context';
import { applyThemeToDom, loadThemeMode, saveThemeMode } from './theme-storage';

export interface ThemeProviderProps {
  children: ReactNode;
}

// Dev-only helper for manual verification. Keep null in normal builds.
const DEV_FORCE_THEME_MODE: ThemeMode | null = null;

/**
 * Manages theme state, persistence, DOM application and system-preference updates.
 */
export function ThemeProvider({ children }: ThemeProviderProps): ReactNode {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (import.meta.env.DEV && DEV_FORCE_THEME_MODE !== null) {
      return DEV_FORCE_THEME_MODE;
    }
    return loadThemeMode();
  });

  const [systemPrefersDark, setSystemPrefersDark] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)').matches : false,
  );

  const effectiveTheme: 'light' | 'dark' = mode === 'system' ? (systemPrefersDark ? 'dark' : 'light') : mode;

  useEffect(() => {
    applyThemeToDom(effectiveTheme);
  }, [effectiveTheme]);

  useEffect(() => {
    if (mode !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    function handleChange(e: MediaQueryListEvent) {
      setSystemPrefersDark(e.matches);
    }
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, [mode]);

  const setTheme = useCallback((newMode: ThemeMode) => {
    saveThemeMode(newMode);
    setModeState(newMode);
  }, []);

  const value = useMemo(() => ({ mode, effectiveTheme, setTheme }), [mode, effectiveTheme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
